import type { StudioForm } from '../studio/studio-api';
import { record, text } from '../studio/studio-content';
import { photoPolicy, type PhotoPolicy } from '../photo/photo-model';
import { PROMPT_MAX } from '../studio/prompt-limit';
export const videoModes = ['text', 'photo', 'style', 'edit', 'product', 'parallax'] as const;
export type VideoMode = (typeof videoModes)[number];
export function videoPolicy(form: StudioForm): (Record<string, unknown> & { mode: VideoMode }) | null {
  const p = record(record(form.constraints).video);
  return p.policy_version === 'video-n8n-v1' && videoModes.includes(p.mode as VideoMode) ? { ...p, mode: p.mode as VideoMode } : null;
}
/** Общие редакторы получают только публичные метаданные, никогда приватные промпты. */
export function videoPhotoPolicy(form: StudioForm): PhotoPolicy | null {
  const p = videoPolicy(form);
  if (!p) return null;
  return photoPolicy({ ...form, constraints: { photo: { ...p, policy_version: 'photo-n8n-v1', mode: 'generate' } } });
}
export const videoLabels: Record<string, string> = {
  photo: 'Первый кадр',
  video: 'Исходное видео',
  description: 'Описание',
  format: 'Формат',
  duration: 'Длительность',
  resolution: 'Разрешение',
  audio: 'Звук от модели',
  reference: 'Свой референс',
  start_ms: 'Начало фрагмента, мс',
  end_ms: 'Конец фрагмента, мс',
};
export function videoMissing(form: StudioForm, inputs: Record<string, string>, rights: boolean, uploads: Record<string, string> = {}) {
  const p = videoPolicy(form);
  if (!p) return 'Этот режим видео пока недоступен';
  if (form.fields.some(f => f.upload && (inputs[f.key] || uploads[f.key])) && !rights) return 'Подтвердите права на материалы';
  const main = p.mode === 'product' ? Number(inputs.main_photo || 1) : 1;
  if (!Number.isInteger(main) || main < 1 || main > 5) return 'Выберите главное фото';
  const mainKey = main === 1 ? 'photo' : `photo_${main}`;
  if (['photo', 'product', 'parallax'].includes(p.mode) && !inputs[mainKey]) return 'Загрузите фото';
  if (p.mode === 'edit' && !inputs.video) return 'Загрузите видео';
  if (p.mode === 'style' && !inputs.photo && !inputs.video) return 'Загрузите фото или видео';
  if (inputs.photo && inputs.video) return 'Выберите один исходный файл';
  const size = Array.from((inputs.description ?? '').trim()).length;
  if (['text', 'edit'].includes(p.mode) && size < Number(p.description_min_length ?? 10)) return 'Добавьте описание, от 10 знаков';
  if (size > Number(p.description_max_length ?? PROMPT_MAX)) return 'Описание длиннее допустимого';
  if (p.mode === 'style' && !inputs.reference && (!inputs.style || inputs.style === 'none')) return 'Выберите стиль или свой референс';
  if (inputs.reference && p.reference_available !== true) return 'Свой референс сейчас недоступен';
  const people = Object.entries(inputs)
    .filter(([key, value]) => /^cast_[1-4]$/.test(key) && value)
    .map(([, value]) => value);
  if (people.length && p.people_available !== true) return 'Люди в кадре недоступны для этого режима';
  if (new Set(people).size !== people.length) return 'Выберите разных людей в кадре';
  if (inputs.video && (inputs.start_ms || inputs.end_ms)) {
    const start = Number(inputs.start_ms ?? 0),
      end = Number(inputs.end_ms);
    if (!Number.isFinite(start) || !Number.isFinite(end) || start < 0 || end - start < 1000 || end - start > 10000)
      return 'Выберите фрагмент от 1 до 10 секунд';
  }
  return form.fields.some(f => f.required && !(p.mode === 'product' && f.key === 'photo') && !inputs[f.key]?.trim())
    ? 'Заполните обязательные поля'
    : null;
}
export function videoDescriptionLabel(mode: VideoMode) {
  return mode === 'edit' || mode === 'style' ? 'Что изменить' : mode === 'photo' || mode === 'product' ? 'Как двигаться' : 'Описание';
}
export function videoDefaults(form: StudioForm): Record<string, string> {
  const p = videoPolicy(form);
  if (!p) return {};
  const result: Record<string, string> = {};
  for (const field of form.fields) {
    const raw = record(field).default_value ?? p[`default_${field.key}`];
    const value = raw === undefined || raw === null ? '' : String(raw);
    if (value && (!field.values.length || field.values.includes(value))) result[field.key] = value;
  }
  const audio = form.fields.find(f => f.key === 'audio');
  if (audio && !result.audio && audio.values.includes(p.mode === 'parallax' ? 'false' : 'true'))
    result.audio = p.mode === 'parallax' ? 'false' : 'true';
  return result;
}
export function videoTool(value: unknown): { app_id: number; mode: VideoMode; title: string } | null {
  const v = record(value);
  return Number.isSafeInteger(v.app_id) && Number(v.app_id) > 0 && videoModes.includes(v.mode as VideoMode) && text(v.title)
    ? { app_id: Number(v.app_id), mode: v.mode as VideoMode, title: text(v.title) }
    : null;
}

export const videoValues: Record<string, string> = {
  src: 'Как у исходника',
  true: 'Со звуком',
  false: 'Без звука',
  5: '5 секунд',
  10: '10 секунд',
};
